import { useState, useEffect } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Heart, Copy, Check, Building2, Smartphone, CreditCard, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RZP_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID;

const UPI_ID = "kaalubhoorifoundatio.82109503@hdfcbank";
const BANK = {
  name: "Kaalu Bhoori Foundation",
  account: "50100850785316 (Savings)",
  ifsc: "HDFC0002000",
};

export default function DonateModal({ open, onOpenChange, defaultTier }) {
  const [step, setStep] = useState("tiers"); // tiers | pay | form
  const [selectedTier, setSelectedTier] = useState(defaultTier || null);
  const [copied, setCopied] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [customAmount, setCustomAmount] = useState("");
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  useEffect(() => {
    const isValidTier = defaultTier && typeof defaultTier === "object" && typeof defaultTier.id === "string";
    if (open && isValidTier) {
      setSelectedTier(defaultTier);
      setStep(defaultTier._jumpToPay ? "pay" : "tiers");
      if (defaultTier.id === "custom") setCustomAmount("");
    } else if (open) {
      setSelectedTier(null);
      setStep("tiers");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, defaultTier]);

  const tiers = [
    {
      id: "feeding-day",
      label: "Sponsor a feeding day",
      amount: "₹3,000",
      amount_inr: 3000,
      cadence: "one time",
      desc: "Supports one full day of route feeding across our monitored community dog zones.",
    },
    {
      id: "female-ster",
      label: "Female dog sterilization",
      amount: "₹4,500",
      amount_inr: 4500,
      cadence: "one time",
      desc: "Sterilization, vaccination, monitoring and recovery support for one female community dog.",
    },
    {
      id: "male-ster",
      label: "Male dog sterilization",
      amount: "₹3,500",
      amount_inr: 3500,
      cadence: "one time",
      desc: "Sterilization, vaccination and monitored recovery for one male community dog.",
    },
    {
      id: "full-care",
      label: "Full inclusion (1 dog · 1 year)",
      amount: "₹11,000",
      amount_inr: 11000,
      cadence: "per year",
      desc: "Sterilization, vaccination, treatment, route integration and monitored follow up for one dog for a full year.",
      featured: true,
    },
    {
      id: "monthly-feeding",
      label: "Sponsor monthly feeding",
      amount: "₹90,000",
      amount_inr: 90000,
      cadence: "per month",
      desc: "Recurring route feeding operations, monitoring and daily field continuity across eight routes.",
    },
  ];

  const handleCopy = async (val) => {
    try {
      await navigator.clipboard.writeText(val);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
      toast.success("Copied to clipboard");
    } catch {
      toast.message("Couldn't copy automatically", { description: val });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        tier: selectedTier?.label || null,
        amount: selectedTier?.amount || null,
        message: form.message || null,
      });
      toast.success("Thank you. We'll get back within 24 hours.");
      setForm({ name: "", email: "", phone: "", message: "" });
      onOpenChange(false);
      setTimeout(() => setStep("tiers"), 300);
    } catch (err) {
      console.error("contact submit failed", err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail;
      toast.error(detail ? String(detail) : "Network hiccup. Please try again or email kaalubhoorifoundation@gmail.com");
    } finally {
      setSubmitting(false);
    }
  };

  const currentAmount = () => {
    if (selectedTier?.amount_inr) return selectedTier.amount_inr;
    const n = parseInt(customAmount, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const handleRazorpay = async () => {
    if (typeof window === "undefined" || !window.Razorpay) {
      toast.error("Payment gateway is loading, please try again in a second.");
      return;
    }
    const amountInr = currentAmount();
    if (amountInr < 10) {
      toast.error("Please enter a valid amount");
      return;
    }
    setPaying(true);
    try {
      const { data } = await axios.post(`${API}/razorpay/order`, {
        amount: amountInr,
        tier: selectedTier?.label || null,
        name: form.name || null,
        email: form.email || null,
        phone: form.phone || null,
      });

      const rzp = new window.Razorpay({
        key: data.key_id || RZP_KEY,
        amount: data.amount,
        currency: data.currency,
        order_id: data.order_id,
        name: "Kaalu Bhoori Foundation",
        description: selectedTier?.label || "Donation",
        image: "https://customer-assets.emergentagent.com/job_bhoori-mission/artifacts/tu01wm1x_WhatsApp%20Image%202025-11-22%20at%209.50.48%20PM.jpeg",
        prefill: {
          name: form.name || "",
          email: form.email || "",
          contact: form.phone || "",
        },
        theme: { color: "#B8743C" },
        handler: async (res) => {
          try {
            await axios.post(`${API}/razorpay/verify`, {
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
              name: form.name || null,
              email: form.email || null,
              phone: form.phone || null,
              tier: selectedTier?.label || null,
              amount: amountInr,
            });
            toast.success("Thank you! Your donation is received. We will email your 80G receipt shortly.");
            onOpenChange(false);
            setTimeout(() => { setStep("tiers"); setCustomAmount(""); }, 300);
          } catch {
            toast.error("Payment received but verification failed. Please email us the payment ID.");
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
      });
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again or use UPI / bank transfer.");
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      console.error("razorpay flow failed", err?.response?.status, err?.response?.data, err);
      const detail = err?.response?.data?.detail;
      toast.error(detail ? String(detail) : "Hiccup starting payment. Please try again, or use UPI / bank transfer below.");
    } finally {
      setTimeout(() => setPaying(false), 500);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="donate-modal"
        className="sm:max-w-[640px] bg-[#F6F2EA] border-[#DED2C2] rounded-3xl p-0 overflow-hidden max-h-[90vh] overflow-y-auto"
      >
        <div className="p-8 md:p-10">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl md:text-4xl text-[#2A1F1A] tracking-tight">
              Stand with Kaalu & Bhoori
            </DialogTitle>
            <DialogDescription className="text-[#5C4E46] text-base">
              Every contribution funds food, vaccinations and trust-based rescue across Indore.
            </DialogDescription>
          </DialogHeader>

          {step === "tiers" && (
            <div className="mt-6 space-y-5" data-testid="donate-tiers">
              <div>
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C] mb-3">Quick give</div>
                <div className="grid grid-cols-4 gap-2">
                  {[250, 500, 1000, 5000].map((a) => (
                    <button
                      key={a}
                      data-testid={`donate-quick-${a}`}
                      onClick={() => { setSelectedTier({ id: `quick-${a}`, label: "One time gift", amount: `₹${a.toLocaleString("en-IN")}`, amount_inr: a }); setStep("pay"); }}
                      className="rounded-2xl border border-[#DED2C2] bg-white px-3 py-3 text-center hover:-translate-y-0.5 hover:border-[#B8743C] hover:shadow-[0_8px_20px_rgba(43,29,24,0.06)] transition-all duration-200"
                    >
                      <div className="font-display text-lg md:text-xl text-[#2A1F1A]">₹{a.toLocaleString("en-IN")}</div>
                    </button>
                  ))}
                </div>
                <button
                  data-testid="donate-custom"
                  onClick={() => { setSelectedTier({ id: "custom", label: "Custom Amount", amount: "Any" }); setStep("pay"); }}
                  className="mt-2 w-full text-center text-sm text-[#B8743C] hover:text-[#99602F] underline-offset-4 hover:underline pt-1"
                >
                  Or contribute a custom amount →
                </button>
              </div>

              <div className="pt-3 border-t border-[#DED2C2]">
                <div className="text-xs font-bold uppercase tracking-[0.22em] text-[#B8743C] mb-3">Sponsorships</div>
                <div className="space-y-3">
                  {tiers.map((t) => (
                    <button
                      key={t.id}
                      data-testid={`donate-tier-${t.id}`}
                      onClick={() => { setSelectedTier(t); setStep("pay"); }}
                      className={`w-full text-left rounded-2xl border p-5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(43,29,24,0.06)] ${
                        t.featured ? "border-[#B8743C] bg-[#F2D5A3]/30" : "border-[#DED2C2] bg-white"
                      }`}
                    >
                      <div className="flex items-baseline justify-between gap-4">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#B8743C]">{t.label}</div>
                          <div className="font-display text-2xl md:text-3xl text-[#2A1F1A] mt-1">
                            {t.amount}
                            <span className="text-sm text-[#5C4E46] ml-2 font-body">{t.cadence}</span>
                          </div>
                        </div>
                        {t.featured && (
                          <span className="text-xs font-medium px-3 py-1 rounded-full bg-[#B8743C] text-[#F6F2EA] whitespace-nowrap">Most needed</span>
                        )}
                      </div>
                      <p className="text-sm text-[#5C4E46] mt-2.5 leading-relaxed">{t.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="mt-3 text-xs text-[#5C4E46] italic">
                  Monthly recurring giving is coming soon. For now, a quarterly contribution works the same way.
                </p>
              </div>
            </div>
          )}

          {step === "pay" && (
            <div className="mt-6" data-testid="donate-pay-step">
              <div className="rounded-2xl bg-[#EFE8DA] border border-[#DED2C2] p-5 mb-5">
                <div className="text-xs uppercase tracking-[0.2em] text-[#B8743C] font-bold">Selected</div>
                <div className="font-display text-2xl text-[#2A1F1A] mt-1">
                  {selectedTier?.label} <span className="text-base text-[#5C4E46]">· {selectedTier?.amount}</span>
                </div>
                {selectedTier?.id === "custom" && (
                  <div className="mt-4">
                    <Label htmlFor="kb-custom-amt" className="text-[#2A1F1A] text-sm">Amount (INR)</Label>
                    <Input
                      id="kb-custom-amt"
                      data-testid="custom-amount-input"
                      type="number"
                      min="10"
                      value={customAmount}
                      onChange={(e) => setCustomAmount(e.target.value)}
                      placeholder="e.g. 1500"
                      className="mt-1 bg-white border-[#DED2C2]"
                    />
                  </div>
                )}
              </div>

              <div className="rounded-2xl border-2 border-[#B8743C] bg-gradient-to-br from-[#F2D5A3]/40 to-[#F6F2EA] p-5 mb-4">
                <div className="flex items-center gap-2 mb-2 text-[#2A1F1A] font-medium">
                  <CreditCard className="w-4 h-4 text-[#B8743C]" /> Pay online
                  <span className="ml-auto text-[11px] font-normal text-[#5C4E46]">Cards · UPI · Netbanking · Wallets</span>
                </div>
                <p className="text-xs text-[#5C4E46] mb-4">Secure checkout via Razorpay. 80G receipt auto-sent to your email.</p>
                <Button
                  data-testid="razorpay-pay-btn"
                  onClick={handleRazorpay}
                  disabled={paying || (selectedTier?.id === "custom" && currentAmount() < 10)}
                  className="w-full bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full h-12 text-base shadow-[0_10px_24px_rgba(184,92,56,0.25)]"
                >
                  {paying ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Opening checkout…</>) : (<><Heart className="w-4 h-4 mr-2" /> Donate ₹{currentAmount() || "0"}</>)}
                </Button>
              </div>

              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-[#DED2C2]"></div></div>
                <div className="relative flex justify-center text-xs uppercase tracking-[0.22em] text-[#5C4E46]"><span className="bg-[#F6F2EA] px-3">or pay directly</span></div>
              </div>

              <div className="space-y-4">
                <div className="rounded-2xl border border-[#DED2C2] p-5">
                  <div className="flex items-center gap-2 mb-3 text-[#2A1F1A] font-medium">
                    <Smartphone className="w-4 h-4 text-[#B8743C]" /> UPI
                  </div>
                  <div className="flex items-center justify-between gap-3 bg-[#F6F2EA] rounded-xl px-4 py-3 border border-[#DED2C2]">
                    <code className="text-sm md:text-base text-[#2A1F1A] font-mono break-all">{UPI_ID}</code>
                    <Button
                      data-testid="copy-upi-btn"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleCopy(UPI_ID)}
                      className="shrink-0 text-[#B8743C] hover:text-[#99602F] hover:bg-[#F2D5A3]/40"
                    >
                      {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-[#5C4E46] mt-2">After paying, share screenshot via the form below for an 80G receipt.</p>
                </div>

                <div className="rounded-2xl border border-[#DED2C2] p-5">
                  <div className="flex items-center gap-2 mb-3 text-[#2A1F1A] font-medium">
                    <Building2 className="w-4 h-4 text-[#B8743C]" /> Bank transfer
                  </div>
                  <div className="text-sm text-[#5C4E46] space-y-1.5">
                    <div><span className="text-[#2A1F1A] font-medium">Account name:</span> {BANK.name}</div>
                    <div className="flex items-center justify-between gap-2"><span><span className="text-[#2A1F1A] font-medium">A/c no:</span> {BANK.account}</span></div>
                    <div className="flex items-center justify-between gap-2"><span><span className="text-[#2A1F1A] font-medium">IFSC:</span> {BANK.ifsc}</span><Button data-testid="copy-ifsc-btn" variant="ghost" size="sm" onClick={()=>handleCopy(BANK.ifsc)} className="text-[#B8743C] hover:bg-[#F2D5A3]/40 h-7 px-2"><Copy className="w-3.5 h-3.5" /></Button></div>
                  </div>
                </div>

                <p className="text-xs text-[#5C4E46] text-center pt-2">
                  Registered under Sections 12A and 80G. Your donation is tax deductible.
                </p>
              </div>

              <DialogFooter className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-between gap-2">
                <Button
                  data-testid="back-to-tiers-btn"
                  variant="ghost"
                  onClick={() => setStep("tiers")}
                  className="text-[#5C4E46] hover:text-[#B8743C]"
                >
                  ← Back
                </Button>
                <Button
                  data-testid="proceed-to-form-btn"
                  onClick={() => setStep("form")}
                  className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-6"
                >
                  Share details for receipt
                </Button>
              </DialogFooter>
            </div>
          )}

          {step === "form" && (
            <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="donate-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="kb-name" className="text-[#2A1F1A]">Name</Label>
                  <Input id="kb-name" data-testid="contact-name" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-1 bg-white border-[#DED2C2]" required />
                </div>
                <div>
                  <Label htmlFor="kb-email" className="text-[#2A1F1A]">Email</Label>
                  <Input id="kb-email" type="email" data-testid="contact-email" value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-1 bg-white border-[#DED2C2]" required />
                </div>
              </div>
              <div>
                <Label htmlFor="kb-phone" className="text-[#2A1F1A]">Phone <span className="text-[#5C4E46] font-normal">(optional)</span></Label>
                <Input id="kb-phone" data-testid="contact-phone" value={form.phone} onChange={(e)=>setForm({...form, phone:e.target.value})} className="mt-1 bg-white border-[#DED2C2]" />
              </div>
              <div>
                <Label htmlFor="kb-msg" className="text-[#2A1F1A]">Message <span className="text-[#5C4E46] font-normal">(optional)</span></Label>
                <Textarea id="kb-msg" data-testid="contact-message" value={form.message} onChange={(e)=>setForm({...form, message:e.target.value})} placeholder="Mention payment date / dedication / sponsorship preference" className="mt-1 bg-white border-[#DED2C2] min-h-[90px]" />
              </div>
              <DialogFooter className="flex flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-2">
                <Button type="button" variant="ghost" onClick={()=>setStep("pay")} className="text-[#5C4E46] hover:text-[#B8743C]">← Back</Button>
                <Button type="submit" disabled={submitting} data-testid="submit-contact-btn" className="bg-[#B8743C] hover:bg-[#99602F] text-[#F6F2EA] rounded-full px-6">
                  {submitting ? "Sending…" : (<><Heart className="w-4 h-4 mr-2" /> Send</>)}
                </Button>
              </DialogFooter>
            </form>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
